import { useCallback, useState } from 'react';
import { AxiosResponse } from 'axios';

import {
  GetDetailsResponseType,
  ReportType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  transformDisclosuresResponse,
  GetDisclosuresResponseType,
  Disclosures,
} from 'QuoteApp/services/endpoints/GetDisclosures';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { useAppContext } from 'QuoteApp/services/providers';
import { useToast } from 'Shared/components';
import { runWithPolling, TIMEOUT_ERROR } from 'QuoteApp/utils';
import {
  getReportMessages,
  ReportChangeMessageType,
  ReportMessageIncidentType,
} from './ReportMessages';
import { ReportStatus } from './types';

interface UseReportRunnerReturnType {
  runReport(
    report: ReportType,
    shouldRefetchRate: boolean
  ): Promise<GetDetailsResponseType>;
  messages: Array<ReportChangeMessageType>;
  productToggled: boolean;
  showRateChangeModal: boolean;
  rateChangeModalData: RateChangeModalData | null;
  setShowRateChangeModal: (show: boolean) => void;
}

export type ReportErrorContext =
  | 'timeout'
  | 'reports completed with error'
  | 'error';

type StatusResponse = {
  status: string;
  message: string;
  decision: string;
};

// We only need one message here, but it needs to be in an array
const createReportFailedMessage = (
  reportType: ReportMessageIncidentType
): Array<ReportChangeMessageType> => [
  {
    reportType: reportType,
    messageType: 'report-failure',
    content: 'Report Failed',
    adverseAction: false,
  },
];

const createProductToggledReportMessage = (
  reportType: ReportMessageIncidentType
): Array<ReportChangeMessageType> => [
  {
    reportType: reportType,
    messageType: 'product-toggle',
    content: 'Product toggled due to report results',
    adverseAction: false,
  },
];

const REPORT_POLL_MAX_ATTEMPTS = 80; // Poll for up to 4 mins to mitigate timeouts

export type RateChangeModalData = {
  initialInstallmentAmount: string;
  newInstallmentAmount: string;
  paymentFrequencyLabel: string;
};

export const useReportRunner = (
  reportType: ReportMessageIncidentType
): UseReportRunnerReturnType => {
  const {
    rate,
    fetchRate,
    refreshDetails,
    setDetails,
    appId,
    changeCanNavigate,
  } = useAppContext();
  const [messages, setMessages] = useState<Array<ReportChangeMessageType>>([]);
  const [productToggled, setProductToggled] = useState(false);
  const [showRateChangeModal, setShowRateChangeModal] = useState(false);
  const [rateChangeModalData, setRateChangeModalData] =
    useState<RateChangeModalData | null>(null);

  const [, getReport] = useLazyQuoteApi<{
    processId: string;
  }>({});
  const [, getStatus] = useLazyQuoteApi<StatusResponse>({});

  const [, requestDisclosure] = useLazyQuoteApi<GetDisclosuresResponseType>({
    transformResponse: transformDisclosuresResponse,
  });

  const [, postDisclosure] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
    transformResponse: transformAppDetails,
  });

  const { toast } = useToast();

  const handleReportError = useCallback(
    (type: ReportErrorContext, context?: Record<string, string>) => {
      setMessages(createReportFailedMessage(reportType));
      toast({
        message: 'An error occured while running reports',
        position: 'top',
        type: 'error',
        withSecondaryNav: false,
        context: { type, ...context },
      });
    },
    [reportType, toast]
  );

  const handleNonReportError = useCallback(() => {
    toast({
      message: 'An error occurred, please try running reports again',
      position: 'top',
      type: 'error',
      withSecondaryNav: false,
    });
    setMessages(createReportFailedMessage(reportType));
  }, [reportType, toast]);

  const runReport = useCallback(
    async function (
      report: ReportType,
      shouldRefreshRate: boolean
    ): Promise<GetDetailsResponseType> {
      setMessages([]);
      changeCanNavigate(false);
      setShowRateChangeModal(false);
      setRateChangeModalData(null);
      const getReportResponse = await getReport({
        method: 'POST',
        url: `insurance_applications/${appId}/reports/${report.reportName}`,
      }).catch((err) => {
        handleNonReportError();
        throw err;
      });

      await runWithPolling<AxiosResponse<StatusResponse>>(
        async () => {
          const response = await getStatus({
            url: `insurance_applications/${appId}/reports/${getReportResponse.data.processId}/status`,
          });
          return response;
        },
        (httpResponse) =>
          httpResponse.status !== 304 &&
          httpResponse.data.status !== ReportStatus.Pending,
        REPORT_POLL_MAX_ATTEMPTS
      )
        .then((response) => response.data)
        .then((response) => {
          if (response?.decision === 'product_toggle') {
            setMessages(createProductToggledReportMessage(reportType));
            setProductToggled(true);
            changeCanNavigate(true);
            throw new Error('product_toggle');
          }
          return response;
        })
        .then((response) => {
          if (response?.status === ReportStatus.Error) {
            handleReportError('reports completed with error');
            throw new Error(response.status);
          }
        })
        .catch((err) => {
          if (err.message !== 'product_toggle') {
            handleReportError(
              err.message === TIMEOUT_ERROR ? 'timeout' : 'error'
            );
          }
          throw new Error(
            err.message === 'product_toggle' ? 'error' : 'timeout'
          );
        })
        .finally(() => {
          // as clue-mvr is the currently running as the last report, canNavigate needs to change after refeshing the rate so when it navigate to the next page the rate is the latests
          if (reportType !== 'clue-mvr' && reportType !== 'clue') {
            changeCanNavigate(true);
          }
        });

      /**
       * NOTE: this refetching is in series because we need to make sure details responds before we refetch rate
       * we don't want details to fail and therefore cover our rate change messaging. this could confuse the user.
       * in the process we might ignore rate call failure, but there isn't a nice way of check that both network requests
       * succeed before setting state, since refreshDetails handles this elsewhere
       */
      return refreshDetails()
        .then(async (details) => {
          if (shouldRefreshRate && details.status === 'open') {
            const newRate = await fetchRate();

            let adverseActionDocumentName: Disclosures | null = null;

            if (reportType === 'ncf-cc') {
              adverseActionDocumentName = Disclosures.FCRAAdverseAction;
            } else if (reportType === 'clue-mvr' || reportType === 'clue') {
              // enables navigation to purchase page when last report has ran and rate has refreshed, boolean check here https://github.com/Clearcover/core-app/blob/master/src/QuoteApp/screens/Reports/Reports.tsx#L205
              await refreshDetails();
              changeCanNavigate(true);
              adverseActionDocumentName = Disclosures.ClueMvrAdverseAction;
            }

            let adverseActionDocument:
              | (typeof details.disclosures)[number]
              | undefined;
            let adverseActionDisclosureText = '';

            if (adverseActionDocumentName) {
              adverseActionDocument = details.disclosures.find(
                (doc) =>
                  doc.attributes.documentName === adverseActionDocumentName
              );
            }

            // if there is an adverse action document and it has not been completed
            if (
              adverseActionDocument &&
              !adverseActionDocument.attributes.completed
            ) {
              const disclosureData = await requestDisclosure({
                url: `insurance_applications/${appId}/document_signatures/${adverseActionDocumentName}`,
              });

              // disclosure text from api
              adverseActionDisclosureText =
                disclosureData?.data?.bodyTextSanitized?.__html;
            }

            const reportChangeMessages = getReportMessages(
              rate,
              newRate,
              'report results',
              reportType,
              adverseActionDisclosureText
            );

            if (reportChangeMessages.length) {
              // set if we have rates and any report change messages
              setMessages(reportChangeMessages);

              if (
                reportChangeMessages.some(
                  (message) => message.modalRateChange
                ) &&
                !details.quoteChangeAcknowledgement
              ) {
                setShowRateChangeModal(true);
                setRateChangeModalData(
                  reportChangeMessages.find(
                    (message) => message.modalRateChange
                  )?.modalRateChange || null
                );
              }

              // after showing the adverse action text complete the disclosure
              if (adverseActionDisclosureText) {
                const completedDisclosureData = await postDisclosure({
                  url: `/insurance_applications/${appId}/document_signatures/${adverseActionDocumentName}/complete`,
                });

                // refresh updated details
                setDetails(completedDisclosureData.data);
              }
            }
          } else if (details.status !== 'open' && reportType === 'clue-mvr') {
            changeCanNavigate(true);
          }

          return details;
        })
        .catch((err) => {
          handleNonReportError();
          throw err;
        });
    },
    [
      changeCanNavigate,
      getReport,
      appId,
      refreshDetails,
      handleNonReportError,
      getStatus,
      handleReportError,
      fetchRate,
      rate,
      reportType,
      requestDisclosure,
      postDisclosure,
      setDetails,
    ]
  );

  return {
    runReport,
    messages,
    productToggled,
    showRateChangeModal,
    rateChangeModalData,
    setShowRateChangeModal,
  };
};
